// src/hooks/useSubscription.ts
// Reactive subscription state for React components.
//
// Reads from useAuthStore.profile.subscription (populated by useFirebaseAuth
// after sign-in). For async access checks, delegates to paymentService which
// owns its own 5-minute cache.
//
// Design principle: synchronous `hasAccess()` is fast and covers 99% of UI
// use-cases (hide/show lock icons). The async `checkAccess()` is available
// when you need a server-verified answer before loading gated content.

import { useCallback } from 'react';
import { useAuthStore, type SubscriptionInfo } from '../stores/useAuthStore';
import {
  canAccessUnit,
  isFreeUnit,
  invalidateSubscriptionCache,
  getCachedSubscription,
} from '../services/paymentService';
import { auth } from '../lib/firebase';

// ── Return type ───────────────────────────────────────────────────────────────

export interface UseSubscriptionReturn {
  /**
   * True when the user has an active subscription OR admin `hasFullAccess`.
   * False for anonymous users, inactive/expired subscriptions, and while the
   * profile is still loading.
   */
  isSubscribed: boolean;

  /** The raw subscription info from the user profile, or null if unavailable. */
  subscription: SubscriptionInfo | null;

  /**
   * True once the auth state has resolved (user is either anonymous or signed
   * in). Use this to avoid showing a locked state briefly before profile loads.
   */
  isLoaded: boolean;

  /**
   * Synchronous access check — reads `profile.subscription.status` and the
   * `hasFullAccess` admin flag. Fast, no network call. Suitable for
   * conditionally rendering lock icons and paywalls.
   */
  hasAccess: (bookId: string, unitId: string) => boolean;

  /**
   * Async access check — calls paymentService (Netlify / Firestore + cache).
   * Use before actually delivering gated content (pages URLs, exercise data).
   */
  checkAccess: (bookId: string, unitId: string) => Promise<boolean>;

  /**
   * Invalidates the local subscription cache and re-fetches from the server.
   * Call after a payment event (BOG return, promo activation) to ensure the
   * store reflects the latest Firestore state.
   */
  refresh: () => Promise<void>;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useSubscription(): UseSubscriptionReturn {
  const user    = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);

  // Profile is considered loaded once the Firebase auth state has resolved
  // (user is never undefined after onAuthStateChanged fires).
  const isLoaded = user !== null;

  // Admin hasFullAccess also counts as subscribed
  const isSubscribed =
    profile?.subscription?.status === 'active' ||
    profile?.hasFullAccess === true;

  // ── Synchronous check ─────────────────────────────────────────────────────

  const hasAccess = useCallback(
    (bookId: string, unitId: string): boolean => {
      // Free unit is always accessible
      if (isFreeUnit(bookId, unitId)) return true;
      // Anonymous / logged-out users cannot access paid content
      if (!user || user.isAnonymous) return false;
      // Subscription or admin access
      return isSubscribed;
    },
    [user, isSubscribed],
  );

  // ── Async server-verified check ───────────────────────────────────────────

  const checkAccess = useCallback(
    (bookId: string, unitId: string): Promise<boolean> =>
      canAccessUnit(bookId, unitId),
    [],
  );

  // ── Cache refresh ─────────────────────────────────────────────────────────

  const refresh = useCallback(async (): Promise<void> => {
    const currentUser = auth.currentUser;
    if (!currentUser || currentUser.isAnonymous) return;
    invalidateSubscriptionCache();
    await getCachedSubscription(currentUser.uid);
  }, []);

  return {
    isSubscribed,
    subscription: profile?.subscription ?? null,
    isLoaded,
    hasAccess,
    checkAccess,
    refresh,
  };
}